import axios from "axios";
import { enqueue, drainQueue, subscribeQueue, listQueue, removeItem, retryItem, clearFailed } from "./offlineQueue";
import { stampPayload as stampShiftOwner } from "./shiftOwner";

export const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

/**
 * Helper para DELETE con motivo obligatorio. Envía el header `X-Delete-Reason`
 * que el middleware backend captura en un ContextVar y `record_audit_log`
 * usa como fallback si el endpoint no lo pasa explícito.
 * El caller debe haber obtenido `reason` desde `useConfirmDelete()` antes.
 */
export function apiDelete(url, reason = "", extra = {}) {
  const trimmed = (reason || "").trim().slice(0, 400);
  return api.delete(url, {
    ...extra,
    headers: {
      ...(extra.headers || {}),
      "X-Delete-Reason": trimmed,
    },
  });
}

// Inject the current shift-owner into HSE mutations before they leave the
// device — this way the stamp survives the offline queue and is attached
// to the record even when the request is replayed hours later from
// IndexedDB.
api.interceptors.request.use((config) => {
  try {
    // El project_id explícito del registro gana sobre el filtro global.
    const body = config.data && typeof config.data === "object" && !(config.data instanceof FormData) ? config.data : null;
    const pid = (body && typeof body.project_id === "string" && body.project_id) || localStorage.getItem("ops_project_id");
    if (pid) { config.headers = config.headers || {}; config.headers["X-Ops-Project"] = pid; }
    return stampShiftOwner(config);
  }
  catch (_e) { return config; }
});

const MUTATION_METHODS = ["post", "put", "patch", "delete"];
// Endpoints that must never be queued offline (auth / binary uploads / imports /
// side-effect-heavy operations that need synchronous validation)
const NEVER_QUEUE = [/\/auth\//, /\/logo/i, /\/tasks\/import$/, /\/exports?/i,
                     /\/template$/, /\/candidates\/[^/]+\/hire$/,
                     /\/send$/,               // emailing endpoints (nomina, estimacion, bitacora)
                     /\/credentials\/pdf$/];  // admin credentials export

function isNetworkError(err) {
  return (
    !err?.response &&
    (err?.code === "ERR_NETWORK" || err?.message === "Network Error" || !navigator.onLine)
  );
}

function shouldQueue(config) {
  const method = (config?.method || "").toLowerCase();
  if (!MUTATION_METHODS.includes(method)) return false;
  const url = config?.url || "";
  if (NEVER_QUEUE.some((rx) => rx.test(url))) return false;
  // Skip multipart/file uploads
  const ct = config?.headers?.["Content-Type"] || config?.headers?.["content-type"] || "";
  if (ct.includes("multipart")) return false;
  if (config?.data instanceof FormData) return false;
  return true;
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err?.config;
    if (isNetworkError(err) && shouldQueue(cfg)) {
      let body = cfg.data;
      if (typeof body === "string") {
        try { body = JSON.parse(body); } catch (_e) { /* keep string */ }
      }
      await enqueue({
        method: cfg.method.toUpperCase(),
        url: cfg.url,
        data: body,
      });
      // Return a synthetic optimistic response so the UI keeps working offline
      const synthetic = {
        ...(body && typeof body === "object" ? body : {}),
        id: `offline-${(globalThis.crypto?.randomUUID?.() || Date.now().toString(36))}`,
        _offline: true,
        created_at: new Date().toISOString(),
      };
      return {
        data: cfg.method.toLowerCase() === "delete" ? { ok: true, _offline: true } : synthetic,
        status: 202,
        statusText: "Accepted (offline queue)",
        headers: {},
        config: cfg,
      };
    }
    return Promise.reject(err);
  }
);

// Auto-drain queue when connection returns
if (typeof window !== "undefined") {
  const tryDrain = async () => {
    if (!navigator.onLine) return;
    const result = await drainQueue(api);
    if (result.synced > 0 || result.failed > 0) {
      window.dispatchEvent(new CustomEvent("offline-queue-synced", { detail: result }));
    }
  };
  window.addEventListener("online", tryDrain);
  // Also try once on load in case items remained from a previous session
  window.addEventListener("load", tryDrain);
}

export { subscribeQueue, drainQueue, listQueue, removeItem, retryItem, clearFailed };

export function apiError(e) {
  const data = e?.response?.data;
  // Downloads con responseType:'blob' entregan el error como Blob de JSON.
  // Adjunto el detail crudo del backend leyendo el blob de forma síncrona
  // vía FileReader → fallback al message genérico si falla.
  if (data && typeof data === "object" && typeof data.text === "function" && data instanceof Blob) {
    // Nota: acá devolvemos algo legible ya, y quien quiera el mensaje real
    // usa apiErrorAsync. La UI síncrona verá "Error al descargar" en vez de
    // "Request failed with status code 404".
    return "Error al descargar el archivo";
  }
  const detail = data?.detail;
  if (detail == null) return e?.message || "Error";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((x) => (x && typeof x.msg === "string" ? x.msg : JSON.stringify(x))).join(" ");
  return String(detail);
}

/** Async variant: si el error es un Blob JSON del backend, lee el detail. */
export async function apiErrorAsync(e) {
  const data = e?.response?.data;
  if (data instanceof Blob) {
    try {
      const txt = await data.text();
      const parsed = JSON.parse(txt);
      const detail = parsed?.detail;
      if (typeof detail === "string") return detail;
      if (Array.isArray(detail))
        return detail.map((x) => (x && typeof x.msg === "string" ? x.msg : JSON.stringify(x))).join(" ");
      if (detail != null) return String(detail);
    } catch (_) { /* fallthrough */ }
  }
  return apiError(e);
}

export const fmtMoney = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

export async function downloadFile(pathOrBlob, filename) {
  // Backward-compat: acepta un path (string) haciendo su propio GET,
  // o un Blob/File ya descargado (por si el caller usó api.get con
  // responseType='blob' antes). Evita romper cuando alguien lo llamó
  // pasando el `res.data` directamente.
  let blob;
  if (typeof pathOrBlob === "string") {
    const res = await api.get(pathOrBlob, { responseType: "blob" });
    blob = res.data;
  } else {
    blob = pathOrBlob;
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
